import type { HTMLAttributes, ReactNode } from 'react';

export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

type VariantsFn = (...args: any[]) => string;

export interface HeadingTextProps extends Omit<HTMLAttributes<HTMLHeadingElement>, 'color'> {
    children: ReactNode;
    /** Semantic heading level (h1-h6) */
    as?: HeadingLevel;
    /** Enable text-wrap: balance to prevent widows */
    balance?: boolean;
    /** Use medium font weight */
    medium?: boolean;
    /** Use semibold font weight */
    semibold?: boolean;
    /** Show dotted underline */
    underline?: boolean;
    className?: string;
}

export interface InlineTextProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
    children: ReactNode;
    /** Use medium font weight */
    medium?: boolean;
    /** Use semibold font weight */
    semibold?: boolean;
    /** Enable tabular numerals for aligned number columns */
    tabularNums?: boolean;
    /** Show dotted underline */
    underline?: boolean;
    className?: string;
}

/**
 * Factory for heading-level text components (Display, Headline, Title).
 * Renders a semantic heading element with tailwind-variants styling.
 */
export function createHeadingTextComponent(
    variants: VariantsFn,
    name: string,
    size: string,
    defaultTag: HeadingLevel = 'h2',
) {
    function Component({
        children,
        as: Tag = defaultTag,
        balance,
        medium,
        semibold,
        underline,
        className,
        ...props
    }: HeadingTextProps) {
        return (
            <Tag className={variants({ size, balance, medium, semibold, underline, className })} {...props}>
                {children}
            </Tag>
        );
    }
    Component.displayName = name;
    return Component;
}

/**
 * Factory for inline text components (Body, Emphasis, Overline).
 * Renders a span element with tailwind-variants styling.
 */
export function createInlineTextComponent(
    variants: VariantsFn,
    name: string,
    size: string,
    fixedVariants?: Record<string, unknown>,
) {
    function Component({ children, medium, semibold, tabularNums, underline, className, ...props }: InlineTextProps) {
        return (
            <span
                className={variants({ size, medium, semibold, tabularNums, underline, ...fixedVariants, className })}
                {...props}
            >
                {children}
            </span>
        );
    }
    Component.displayName = name;
    return Component;
}
